import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CardComponentProps } from '@/lib/cards';
import { getUsers, UserSummary } from '@/api/users';

const FILTER_EVENT = 'catty:users-filter-changed';

interface FilterPayload {
  q?: string;
  tier?: string;
}

/**
 * Users Table Card
 * Displays list of users with pagination support
 */
export const UsersTableCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterPayload>({});
  const [hoveredUserId, setHoveredUserId] = useState<string | null>(null);

  const fetchUsers = useCallback(async (filter: FilterPayload, cursor?: string) => {
    try {
      const response = await getUsers({
        q: filter.q || undefined,
        orgId: filter.tier ? undefined : undefined, // tier filtering is client-side for now
        cursor,
        limit: 25,
      });
      return response;
    } catch (err) {
      throw err;
    }
  }, []);

  async function loadInitial(filter: FilterPayload) {
    try {
      setLoading(true);
      const response = await fetchUsers(filter);
      let items = response.items;
      if (filter.tier) {
        items = items.filter((u) => u.tier === filter.tier);
      }
      setUsers(items);
      setNextCursor(response.nextCursor);
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!nextCursor) return;
    try {
      setLoadingMore(true);
      const response = await fetchUsers(activeFilter, nextCursor);
      let items = response.items;
      if (activeFilter.tier) {
        items = items.filter((u) => u.tier === activeFilter.tier);
      }
      setUsers((prev) => [...prev, ...items]);
      setNextCursor(response.nextCursor);
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    loadInitial(activeFilter);
  }, [onError]);

  useEffect(() => {
    function onFilterChange(e: Event) {
      const filter = (e as CustomEvent<FilterPayload>).detail;
      setActiveFilter(filter);
      loadInitial(filter);
    }
    window.addEventListener(FILTER_EVENT, onFilterChange);
    return () => window.removeEventListener(FILTER_EVENT, onFilterChange);
  }, [fetchUsers]);

  const getTierColor = (tier: string) => {
    const colors: Record<string, string> = {
      system_admin: '#dc3545',
      org_admin: '#ffc107',
      subscriber: '#0066cc',
      member: '#28a745',
    };
    return colors[tier] || '#999';
  };

  if (loading) {
    return <div style={{ padding: '1rem', color: '#666' }}>Loading users...</div>;
  }

  if (users.length === 0) {
    return <div style={{ padding: '1rem', color: '#666' }}>No users found</div>;
  }

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.95rem',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Name</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Email</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Tier</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 600 }}>Scope</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr
                key={user.id}
                style={{
                  borderBottom: '1px solid #eee',
                  backgroundColor: hoveredUserId === user.id ? '#f9f9f9' : 'transparent',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={() => setHoveredUserId(user.id)}
                onMouseLeave={() => setHoveredUserId(null)}
              >
                <td style={{ padding: '0.75rem' }}>
                  <Link
                    to={`/users/${user.id}`}
                    style={{ color: '#0066cc', textDecoration: 'none' }}
                  >
                    {user.displayName}
                  </Link>
                </td>
                <td style={{ padding: '0.75rem' }}>{user.email}</td>
                <td style={{ padding: '0.75rem' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      backgroundColor: getTierColor(user.tier),
                      color: 'white',
                      borderRadius: '3px',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                    }}
                  >
                    {user.tier.replace(/_/g, ' ')}
                  </span>
                </td>
                <td style={{ padding: '0.75rem', color: '#666', fontSize: '0.9rem' }}>
                  {user.scopeLevel ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {nextCursor && (
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <button
            onClick={loadMore}
            disabled={loadingMore}
            style={{
              padding: '0.6rem 1.5rem',
              backgroundColor: '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loadingMore ? 'not-allowed' : 'pointer',
              opacity: loadingMore ? 0.6 : 1,
            }}
          >
            {loadingMore ? 'Loading...' : 'Load more users'}
          </button>
        </div>
      )}
    </div>
  );
};

UsersTableCard.displayName = 'UsersTableCard';

export default UsersTableCard;
